import { ReactElement, Children, useState, useEffect } from 'react';

import Button from './Button';

import { EnumTeamContext } from '@/types/team';
import { cn } from '@/utils';

type StepWizardProps = {
  children: ReactElement[];
  onSubmit: () => void;
  getCurrentStep?: (currentStep: number) => void;
  dotsPosition?: 'top';
  hideActions?: boolean;
  isControlled?: boolean;
  stepControlled?: number;
  context?: EnumTeamContext;
  hideSteps?: boolean;
};

export const StepWizard = ({
  children,
  onSubmit,
  getCurrentStep,
  stepControlled,
  dotsPosition,
  isControlled,
  hideSteps,
}: StepWizardProps) => {
  const totalSteps = Children.count(children);
  const [activeStep, setActiveStep] = useState(0);
  const currentStep = Children.toArray(children[activeStep]);
  const isLastStep = !(activeStep + 1 < totalSteps);

  useEffect(() => {
    getCurrentStep?.(activeStep);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep]);

  useEffect(() => {
    if (isControlled && stepControlled !== undefined) {
      setActiveStep(stepControlled);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepControlled]);

  const handleNextStep = () => {
    if (!isLastStep) {
      setActiveStep((prevIndex) => prevIndex + 1);
    }
  };

  const handlePrev = () => {
    if (activeStep > 0) {
      setActiveStep((prevIndex) => prevIndex - 1);
    }
  };

  const arrowPrevButtonControlled = (
    <Button
      className="fixed top-[.55rem] left-1"
      type="button"
      variant="text"
      onClick={() =>
        stepControlled !== undefined && setActiveStep(stepControlled - 1)
      }
    >
      <svg
        fill="none"
        height="35"
        viewBox="0 0 35 35"
        width="35"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M20.1693 27.7085C19.9514 27.7093 19.7361 27.6612 19.5393 27.5678C19.3424 27.4744 19.169 27.3381 19.0318 27.1689L11.988 18.419C11.7735 18.158 11.6562 17.8307 11.6562 17.4929C11.6562 17.1551 11.7735 16.8278 11.988 16.5669L19.2797 7.81687C19.5272 7.51905 19.8829 7.33176 20.2685 7.29621C20.6542 7.26066 21.0381 7.37975 21.3359 7.62728C21.6337 7.87482 21.821 8.23052 21.8566 8.61614C21.8921 9.00177 21.773 9.38572 21.5255 9.68353L15.0068 17.5002L21.3068 25.3169C21.4851 25.5309 21.5984 25.7916 21.6332 26.068C21.668 26.3444 21.6229 26.6251 21.5033 26.8767C21.3836 27.1283 21.1944 27.3403 20.9579 27.4877C20.7215 27.6352 20.4478 27.7118 20.1693 27.7085Z"
          fill="#A09EB3"
        />
      </svg>
    </Button>
  );

  return (
    <main
      className={cn({
        'flex flex-wrap w-full relative': dotsPosition === 'top',
      })}
    >
      {((isControlled && activeStep > 0) ||
        (stepControlled !== undefined && stepControlled > 0)) &&
        arrowPrevButtonControlled}
      <div className="order-1 w-full">{currentStep}</div>
      {!hideSteps && (
        <ul
          className={cn('flex justify-center leading-[0] m-auto', {
            'order-2 my-4 w-full': dotsPosition === 'top',
          })}
        >
          {[...Array(totalSteps).keys()].map((_step, i) => {
            return (
              <li key={i}>
                <i
                  className={cn('dots-wizard', {
                    active: i === activeStep,
                    allStepsPrev: isControlled && i <= activeStep,
                  })}
                />
              </li>
            );
          })}
        </ul>
      )}
      {!isControlled ? (
        isLastStep ? (
          <div className="flex gap-x-2 my-7 lg:mt-7 xl:mt-10 order-3">
            <Button
              className="w-full bg-transparent border-none"
              type="button"
              variant="secondary"
              onClick={handlePrev}
            >
              Volver
            </Button>
            <Button
              className="w-full"
              type="button"
              variant="primary"
              onClick={onSubmit}
            >
              Continuar
            </Button>
          </div>
        ) : (
          <Button
            className="w-full my-7 lg:mt-7 xl:mt-10 order-3"
            type="button"
            variant="primary"
            onClick={handleNextStep}
          >
            Continuar
          </Button>
        )
      ) : undefined}
    </main>
  );
};
